import { convertFromRaw, convertToRaw } from "draft-js";
import {
  fetchDocMetadataService,
  fetchSharedDocMetadataService,
  addDocMetadataService,
  addSharedDocMetadataService,
  updateDocMetadataService,
  updateSharedDocMetadataService,
  deleteDocMetadataService,
  deleteSharedDocMetadataService,
  detectDocMetadataService,
  detectSharedDocMetadataService,
  fetchDocAnalysisService,
  fetchSharedDocAnalysisService,
  provideDocFeedbackService,
  provideSharedDocFeedbackService,
  fetchClauseAnalysisMetaService,
} from "./metalensServices";
import store from "../../store";
import { getFieldValue } from "../../utils/formFieldUtils";

const _transformFormFields = fieldsUsed => {
  const fieldsUsedMap = {};
  if (fieldsUsed && Array.isArray(fieldsUsed)) {
    for (let index = 0; index < fieldsUsed.length; index += 1) {
      const element = fieldsUsed[index];
      fieldsUsedMap[element.id] = element;
    }
  }
  return fieldsUsedMap;
};

const _transformText = (rawData = {}, fieldsUsed = {}) => {
  const { blocks, entityMap } = rawData;

  if (blocks && Array.isArray(blocks)) {
    const newRawData = {
      entityMap,
      blocks: [],
    };
    for (let i = 0; i < blocks.length; i += 1) {
      const block = blocks[i];
      const { text, entityRanges, inlineStyleRanges } = block;
      let textWithEntities = text;
      const newInliniStyleRanges = inlineStyleRanges;
      if (entityMap && entityRanges && entityRanges.length) {
        const sortedEntityRanges = entityRanges.sort(
          (a, b) => a.offset - b.offset,
        );
        let diffOffset = 0;
        let inlineDiff = 0;
        for (let index = 0; index < sortedEntityRanges.length; index += 1) {
          const entityElement = sortedEntityRanges[index];
          let replacementText = textWithEntities.slice(
            entityElement.offset + diffOffset,
            entityElement.offset + diffOffset + entityElement.length,
          );

          if (
            entityMap[entityElement.key] &&
            entityMap[entityElement.key].data &&
            entityMap[entityElement.key].data.dynamicFieldId &&
            fieldsUsed &&
            fieldsUsed[entityMap[entityElement.key].data.dynamicFieldId] &&
            fieldsUsed[entityMap[entityElement.key].data.dynamicFieldId].value
          ) {
            replacementText =
              getFieldValue(
                fieldsUsed[entityMap[entityElement.key].data.dynamicFieldId],
                "USD",
              ) || "";
          }
          textWithEntities =
            textWithEntities.slice(0, entityElement.offset + diffOffset) +
            replacementText +
            textWithEntities.slice(
              diffOffset + entityElement.offset + entityElement.length,
            );
          diffOffset =
            diffOffset + replacementText.length - entityElement.length;
          // change inline styles
          let isInlineDiff = false;
          for (let j = 0; j < newInliniStyleRanges.length; j += 1) {
            const inlineStyle = newInliniStyleRanges[j];
            const newInlineStyle = { ...inlineStyle };
            if (
              inlineStyle.offset <= entityElement.offset + inlineDiff &&
              inlineStyle.offset + inlineStyle.length >=
                entityElement.offset + inlineDiff + entityElement.length
            ) {
              newInlineStyle.length +=
                replacementText.length - entityElement.length;
            } else if (
              inlineStyle.offset >=
              entityElement.offset + entityElement.length
            ) {
              newInlineStyle.offset +=
                replacementText.length - entityElement.length;
              isInlineDiff = true;
            }
            newInliniStyleRanges[j] = { ...newInlineStyle };
          }
          if (isInlineDiff) {
            inlineDiff += replacementText.length - entityElement.length;
          }
        }
      }
      // Remove form fields as the text is replaced
      newRawData.blocks.push({
        ...block,
        text: textWithEntities,
        entityRanges: [],
        inlineStyleRanges: newInliniStyleRanges,
      });
    }
    return newRawData;
  }
  return rawData;
};

const _transformEditorState = (
  data,
  fieldsUsedMap = {},
  spotlightEntities = null,
  mentions = null,
) => {
  const rawData = convertToRaw(data);
  let newRawData = rawData;
  // if no form fields present
  if (Object.keys(fieldsUsedMap).length) {
    newRawData = _transformText(rawData, fieldsUsedMap);
  }
  // save this new Raw Data
  let newData = convertFromRaw(newRawData);
  // also add entities
  if (spotlightEntities) {
    spotlightEntities.forEach(se => {
      newData = newData.createEntity("SPOTLIGHT", "MUTABLE", se);
    });
  }
  if (mentions) {
    //
  }
  return newData;
};

const _transformTable = (
  tableComponent,
  fieldsUsedMap,
  spotlightEntities,
  mentions,
) => {
  const { content } = tableComponent || {};
  const { data } = content;
  const { data: dataArray } = data || {};
  const newDataArray = [];
  if (dataArray && Array.isArray(dataArray)) {
    dataArray.forEach(dataElement => {
      const { data: dataElementArray } = dataElement || {};
      // const newDataElement = {...dataElement};
      if (dataElementArray && Array.isArray(dataElementArray)) {
        const newDataElementArray = [];
        dataElementArray.forEach(element => {
          const { data: dataObject } = element;
          const { data: dataBlock } = dataObject || {};
          const newData = _transformEditorState(
            dataBlock,
            fieldsUsedMap,
            spotlightEntities,
            mentions,
          );
          newDataElementArray.push({
            ...element,
            data: {
              ...dataObject,
              data: newData,
            },
          });
        });
        const newDataElement = {
          ...dataElement,
          data: newDataElementArray,
        };
        newDataArray.push(newDataElement);
      } else {
        newDataArray.push(dataElement);
      }
    });
  } else {
    return tableComponent;
  }
  return {
    ...tableComponent,
    content: {
      ...content,
      data: {
        ...data,
        data: newDataArray,
      },
    },
  };
};

const _getNewComponentMultiple = (
  componentsMap,
  fieldsUsedMap,
  spotlightEntities,
  mentions,
) => {
  const newComponentsMap = { ...componentsMap };
  if (typeof componentsMap === "object") {
    Object.keys(componentsMap).forEach(key => {
      const component = componentsMap[key] || {};
      const { content } = component;
      const { type, data } = content || {};
      if (type === "HTML") {
        const newData = _transformEditorState(
          data,
          fieldsUsedMap,
          spotlightEntities,
          mentions,
        );
        newComponentsMap[key] = {
          ...component,
          content: {
            ...content,
            data: newData,
          },
        };
      } else if (type === "Table") {
        newComponentsMap[key] = _transformTable(
          component,
          fieldsUsedMap,
          spotlightEntities,
          mentions,
        );
      }
    });
  }
  return newComponentsMap;
};

const _getNewComponent = (
  components,
  componentID,
  fieldsUsedMap,
  spotlightEntities = null,
  mentions = null,
) => {
  const docComponent = components[componentID];
  const { content } = docComponent || {};
  const { type: componentType, data } = content || {};
  if (componentType === "HTML") {
    const newData = _transformEditorState(
      data,
      fieldsUsedMap,
      spotlightEntities,
      mentions,
    );
    // form new component
    return {
      ...docComponent,
      content: {
        ...content,
        data: newData,
      },
    };
  }
  if (componentType === "CoverPage") {
    const { components: coverpageComponents = {} } = data || {};
    // return coverpageComponents;
    const newCoverpageComponents = _getNewComponentMultiple(
      coverpageComponents,
      fieldsUsedMap,
      spotlightEntities,
      mentions,
    );
    return {
      ...docComponent,
      content: {
        ...content,
        data: {
          ...data,
          components: newCoverpageComponents,
        },
      },
    };
  }
  if (componentType === "Table") {
    return _transformTable(
      docComponent,
      fieldsUsedMap,
      spotlightEntities,
      mentions,
    );
  }
  if (componentType === "HeaderFooter") {
    const { header, footer } = data || {};
    const { components: headerComponents = {} } = header || {};
    const { components: footerComponents = {} } = footer || {};
    const newHeaderComponents = _getNewComponentMultiple(
      headerComponents,
      fieldsUsedMap,
      spotlightEntities,
      mentions,
    );
    const newFooterComponents = _getNewComponentMultiple(
      footerComponents,
      fieldsUsedMap,
      spotlightEntities,
      mentions,
    );

    return {
      ...docComponent,
      content: {
        ...content,
        data: {
          ...data,
          header: {
            ...header,
            components: newHeaderComponents,
          },
          footer: {
            ...footer,
            components: newFooterComponents,
          },
        },
      },
    };
  }
  return components[componentID];
};

const _transformComponents = (metadata = null, analysis = null) => {
  const {
    componentsCopy: components,
    document: { fields_used: fieldsUsed, template_type: templateType } = {},
  } = store.getState().DocumentStore;
  const newComponents = {};
  const fieldsUsedMap = _transformFormFields(fieldsUsed);
  if (analysis && components) {
    for (let i = 0; i < analysis.length; i += 1) {
      const { id: componentID, block_entities: spotlightEntities } =
        analysis[i];
      // donot run replace unless
      if (fieldsUsed && fieldsUsed.length && templateType === "form") {
        newComponents[componentID] = _getNewComponent(
          components,
          componentID,
          fieldsUsedMap,
          spotlightEntities,
          null,
        );
      }
    }
    return [newComponents, null, null];
  }
  if (metadata && components) {
    const blockMetadataMap = {};
    for (let i = 0; i < metadata.length; i += 1) {
      const { mentions, id: metadataId } = metadata[i];
      if (mentions && Array.isArray(mentions)) {
        for (let j = 0; j < mentions.length; j += 1) {
          const mention = mentions[j];
          const { component_data: { id: componentID, blockId } = {} } = mention;
          // make map for optimise during render
          if (blockMetadataMap[blockId]) {
            blockMetadataMap[blockId].push({ ...mention, metadataId });
          } else {
            blockMetadataMap[blockId] = [{ ...mention, metadataId }];
          }
          // why run again if its only to replace text.
          if (
            !newComponents[componentID] &&
            fieldsUsed &&
            fieldsUsed.length &&
            templateType === "form"
          ) {
            newComponents[componentID] = _getNewComponent(
              components,
              componentID,
              fieldsUsedMap,
              null,
              mentions,
            );
          }
        }
      }
    }
    return [newComponents, null, blockMetadataMap];
  }
  return [null, null, null];
};

export const getDocMetadata =
  (docId, sharedHash = null) =>
  dispatch =>
    new Promise((resolve, reject) => {
      const fetchDocService = sharedHash
        ? fetchSharedDocMetadataService
        : fetchDocMetadataService;
      fetchDocService(docId, sharedHash)
        .then(response => {
          // remove line
          const [newComponents, blockSpotlightMap, blockMetadataMap] =
            _transformComponents(response.data, null);
          if (newComponents && Object.keys(newComponents).length) {
            dispatch({
              type: "UPDATE_COMPONENTS_QUICKREVIEW",
              payload: {
                newComponents,
              },
            });
          }
          const payload = {
            metadata: response.data,
            lastActive: response.last_activity_time,
            blockSpotlightMap,
            blockMetadataMap,
          };
          dispatch({
            type: "GET_DOC_METADATA",
            payload,
          });
          resolve(response.data);
        })
        .catch(err => {
          reject(err);
        });
    });

export const addDocMetaData =
  (docId, data, sharedHash = null) =>
  dispatch =>
    new Promise((resolve, reject) => {
      const addService = sharedHash
        ? addSharedDocMetadataService
        : addDocMetadataService;
      addService(docId, data, sharedHash)
        .then(response => {
          const payload = {
            newMetaData: response.data,
            lastActive: response.last_activity_time,
          };
          dispatch({
            type: "ADD_NEW_DOC_METADATA",
            payload,
          });
          resolve(response.data);
        })
        .catch(err => {
          reject(err);
        });
    });

// EDIT
export const updateDocMetaData =
  (docID, data, sharedHash = null) =>
  dispatch =>
    new Promise((resolve, reject) => {
      const updateService = sharedHash
        ? updateSharedDocMetadataService
        : updateDocMetadataService;
      updateService(docID, data, sharedHash)
        .then(response => {
          const payload = {
            updatedMetaItem: response.data,
            lastActive: response.last_activity_time,
          };
          dispatch({
            type: "UPDATE_DOC_METADATA",
            payload,
          });
          resolve(response.data);
        })
        .catch(err => {
          reject(err);
        });
    });

// DELETE
export const deleteDocMetaData = (docID, id, sharedHash) => dispatch =>
  new Promise((resolve, reject) => {
    const deleteService = sharedHash
      ? deleteSharedDocMetadataService
      : deleteDocMetadataService;
    deleteService(docID, id, sharedHash)
      .then(response => {
        const payload = {
          id,
          lastActive: response.last_activity_time,
        };
        dispatch({
          type: "DELETE_DOC_METADATA",
          payload,
        });
        resolve(response.data);
      })
      .catch(err => {
        reject(err);
      });
  });

//
export const detectDocMetadata =
  (docId, sharedHash = null) =>
  dispatch =>
    new Promise((resolve, reject) => {
      const detectService = sharedHash
        ? detectSharedDocMetadataService
        : detectDocMetadataService;
      detectService(docId, sharedHash)
        .then(res => {
          // remove line
          const [newComponents, blockSpotlightMap, blockMetadataMap] =
            _transformComponents(res.data, null);
          const payload = {
            metadata: res.data,
            lastActive: res.last_activity_time,
            blockSpotlightMap,
            blockMetadataMap,
          };
          if (newComponents && Object.keys(newComponents).length) {
            dispatch({
              type: "UPDATE_COMPONENTS_QUICKREVIEW",
              payload: {
                newComponents,
              },
            });
          }
          dispatch({
            type: "GET_DOC_METADATA",
            payload,
          });
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });

export const setCommonHash = () => dispatch =>
  dispatch({
    type: "SET_COMMON_META_HASH",
  });

export const setMetalensActiveStatus = active => dispatch =>
  dispatch({
    type: "SET_METALENS_STATUS",
    payload: {
      active,
    },
  });

export const setMetalensTutorialActiveStatus = active => dispatch =>
  dispatch({
    type: "SET_METALENS_TUTORIAL_STATUS",
    payload: {
      active,
    },
  });

export const getDocAnalysis =
  (docID, sharedHash = null) =>
  dispatch =>
    new Promise((resolve, reject) => {
      let getDocAnalysisService = fetchDocAnalysisService;
      if (sharedHash) {
        getDocAnalysisService = fetchSharedDocAnalysisService;
      }
      getDocAnalysisService(docID, sharedHash)
        .then(response => {
          if (response && response.analysis) {
            // remove line
            const [newComponents, blockSpotlightMap, blockMetadataMap] =
              _transformComponents(null, response.analysis.full_analysis);
            if (newComponents && Object.keys(newComponents).length) {
              dispatch({
                type: "UPDATE_COMPONENTS_QUICKREVIEW",
                payload: {
                  newComponents,
                },
              });
            }

            dispatch({
              type: "FETCH_DOC_ANALYSIS",
              payload: {
                fullAnalysis: response.analysis.full_analysis,
                perDepartmentAnalysisIndex:
                  response.analysis.per_department_analysis_index,
                perClauseAnalysisIndex:
                  response.analysis.per_clause_analysis_index,
                spotlightIndex: response.analysis.spotlight_index,
                blockSpotlightMap,
                blockMetadataMap,
              },
            });
          }

          resolve(response);
        })
        .catch(err => {
          reject(err);
        });
    });

export const provideFeedback =
  (docID, componentID, data, sharedHash = null) =>
  dispatch =>
    new Promise((resolve, reject) => {
      let provideFeedbackService = provideDocFeedbackService;
      if (sharedHash) {
        provideFeedbackService = provideSharedDocFeedbackService;
      }
      provideFeedbackService(docID, componentID, data, sharedHash)
        .then(response => {
          dispatch({
            type: "DOC_ANALYSIS_PROVIDE_FEEDBACK",
            payload: response,
          });
          resolve(response);
        })
        .catch(error => {
          reject(error);
        });
    });

export const removeQuickReviewData = () => dispatch =>
  dispatch({
    type: "REMOVE_QUICK_REVIEW_DATA",
  });

export const getClauseAnalysisMeta = () => dispatch =>
  new Promise((resolve, reject) => {
    fetchClauseAnalysisMetaService()
      .then(response => {
        dispatch({
          type: "FETCH_CLAUSE_ANALYSIS_META",
          payload: {
            clauseDepartmentMapping: response.clauseDepartmentMapping,
            departmentClauseMapping: response.departmentClauseMapping,
            entityTypeInfo: response.entityTypeInfo,
          },
        });
        resolve(response);
      })
      .catch(err => {
        reject(err);
      });
  });

export const highlightSpotlightBlockEntities =
  (entities, componentID) => dispatch =>
    dispatch({
      type: "HIGHLIGHT_SPOTLIGHT",
      payload: {
        entities,
        componentID,
      },
    });

export const removeSpotlightBlockEntities = () => dispatch =>
  dispatch({
    type: "REMOVE_HIGHLIGHT_SPOTLIGHT",
  });

export const reverseUpdateComponentQuickReview = () => dispatch => {
  // if form fields are not there, dont reverse
  const {
    document: { fields_used: fieldsUsed, template_type: templateType } = {},
  } = store.getState().DocumentStore;
  if (!(fieldsUsed && fieldsUsed.length) || templateType !== "form") {
    return null;
  }
  return dispatch({
    type: "REVERSE_UPDATE_COMPONENTS_QUICKREVIEW",
  });
};

export const copyComponentsQuickReview = () => dispatch =>
  dispatch({
    type: "COPY_COMPONENTS_QUICKREVIEW",
  });
